import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";

export const BIZ = {
  name: "Pinnacle Vastu Consultants",
  brand: "Pinnacle Vastu",
  email: "info@pinnaclevastu.in",
  phone: "+91 91151 11046",
  address: "40 Ft Road, Aggarwal Colony Main Road, Bhatti Road, Bathinda, Punjab 151001, India",
  website: "https://www.acharyavikulbansal.in",
};
const UPDATED = "June 2026";

function Shell({ title, children, testid }) {
  return (
    <div className="min-h-screen bg-background" data-testid={testid}>
      <div className="max-w-3xl mx-auto px-5 py-12">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{BIZ.brand}</div>
        <h1 className="font-heading text-4xl font-semibold mt-1">{title}</h1>
        <div className="text-xs text-muted-foreground mt-1">Last updated: {UPDATED}</div>
        <div className="mt-8 space-y-5 text-[15px] leading-relaxed [&_h2]:font-heading [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">{children}</div>
        <Contact />
      </div>
    </div>
  );
}

function Contact() {
  return (
    <div className="mt-10 rounded-lg border border-border p-4 text-sm" data-testid="legal-contact">
      <div className="font-semibold">{BIZ.name}</div>
      <div className="text-muted-foreground">{BIZ.address}</div>
      <div className="text-muted-foreground">Email: <a className="underline" href={`mailto:${BIZ.email}`}>{BIZ.email}</a> · Phone: {BIZ.phone}</div>
      <div className="text-muted-foreground">Website: <a className="underline" href={BIZ.website}>{BIZ.website}</a></div>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <Shell title="Privacy Policy" testid="privacy-page">
      <p>{BIZ.name} ("we", "us") operates the {BIZ.brand} website ({BIZ.website}), our Facebook/Instagram lead forms and our internal CRM. This policy explains what personal information we collect, how we use it and your rights.</p>
      <h2>1. Information we collect</h2>
      <ul>
        <li>Details you submit in our website forms or Facebook/Instagram Lead Ads forms: name, mobile number, email, city and any message or answers you provide.</li>
        <li>Ad context supplied by Meta with a lead (form name, ad / campaign name, submission time).</li>
        <li>Records of our communication with you: calls, WhatsApp messages, follow-up notes and course enrollment details.</li>
        <li>For our staff using the CRM: name, work email, phone and Facebook Page access granted by them when connecting a Page.</li>
      </ul>
      <h2>2. How we use it</h2>
      <ul>
        <li>To contact you about the course, consultation or service you enquired about (call, WhatsApp, SMS or email).</li>
        <li>To assign your enquiry to a relationship manager, schedule follow-ups and manage enrollments, payments and student support.</li>
        <li>To improve our services and measure which advertisements bring useful enquiries.</li>
      </ul>
      <h2>3. Facebook / Meta data</h2>
      <p>When a Page admin connects their Facebook Page to our CRM, we use Meta's APIs only to receive Lead Ads submissions made to that Page and to list its lead forms. We store a Page access token solely for this purpose. We do not post on your behalf, read private messages, or sell any Facebook data. Access can be removed at any time from the CRM ("Remove") or from Facebook Settings → Business Integrations.</p>
      <h2>4. Sharing</h2>
      <p>We do not sell personal information. We share it only with service providers that help us operate (cloud hosting, WhatsApp Business messaging provider, email provider, telephony) under confidentiality obligations, or when required by law.</p>
      <h2>5. Retention & security</h2>
      <p>We keep enquiry and student records for as long as needed to provide our services and meet legal/accounting obligations, then delete or anonymise them. Data is stored on secured cloud servers with access limited to authorised staff.</p>
      <h2>6. Your rights</h2>
      <p>You may ask to access, correct or delete your information, or opt out of marketing messages at any time by emailing {BIZ.email} or calling {BIZ.phone}. See our <a className="underline" href="/data-deletion">Data Deletion</a> page.</p>
      <h2>7. Changes</h2>
      <p>We may update this policy; the latest version will always be available on this page.</p>
    </Shell>
  );
}

export function Terms() {
  return (
    <Shell title="Terms of Service" testid="terms-page">
      <p>These terms apply to the {BIZ.brand} website, lead forms and CRM operated by {BIZ.name}.</p>
      <h2>1. Enquiries</h2>
      <p>By submitting a form you confirm the details are yours and agree that our team may contact you by phone, WhatsApp, SMS or email about your enquiry. You can opt out at any time.</p>
      <h2>2. Courses & consultations</h2>
      <p>Course content, schedules, fees and refund terms are communicated at the time of enrollment and form part of these terms. Vastu, astrology and numerology guidance is advisory in nature.</p>
      <h2>3. CRM use (staff & partners)</h2>
      <p>The CRM is for authorised staff and franchise partners only. Users must keep credentials confidential and use customer data only for legitimate business purposes. Connecting a Facebook Page authorises us to receive that Page's Lead Ads data as described in our <a className="underline" href="/privacy-policy">Privacy Policy</a>.</p>
      <h2>4. Liability</h2>
      <p>Services are provided on an "as is" basis. To the extent permitted by law, our liability is limited to the fees paid for the specific service.</p>
      <h2>5. Governing law</h2>
      <p>These terms are governed by the laws of India; courts at Bathinda, Punjab have jurisdiction.</p>
    </Shell>
  );
}

export function DataDeletion() {
  const [params] = useSearchParams();
  const code = params.get("code");
  const [st, setSt] = useState(null);
  useEffect(() => { if (code) api.get(`/meta/data-deletion/${code}`).then((r) => setSt(r.data)).catch(() => setSt({ status: "not_found" })); }, [code]);
  return (
    <Shell title="User Data Deletion" testid="data-deletion-page">
      {code && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-4" data-testid="deletion-status">
          <div className="font-semibold">Request {code}</div>
          <div className="text-sm">Status: <b>{st ? (st.status === "completed" ? "Completed — your Facebook-linked data has been deleted." : st.status === "not_found" ? "Not found" : "Received — processing") : "…"}</b></div>
          {st?.completed_at && <div className="text-xs text-muted-foreground">Completed on {new Date(st.completed_at).toLocaleString("en-IN")}</div>}
        </div>
      )}
      <p>You can ask {BIZ.name} to delete the personal information we hold about you at any time.</p>
      <h2>If you connected a Facebook Page to our CRM</h2>
      <ul>
        <li>Go to Facebook → Settings & privacy → Settings → <b>Business Integrations</b> (or Apps and Websites), find our app and click <b>Remove</b>.</li>
        <li>Facebook will notify us automatically and we delete the stored Page access tokens and account link. You will receive a confirmation code you can check on this page.</li>
      </ul>
      <h2>If you submitted an enquiry / lead form</h2>
      <ul>
        <li>Email <a className="underline" href={`mailto:${BIZ.email}?subject=Data%20deletion%20request`}>{BIZ.email}</a> or call/WhatsApp {BIZ.phone} with the mobile number you used.</li>
        <li>We will delete or anonymise your enquiry, call notes and messages within 30 days, except records we must keep by law (e.g. invoices).</li>
      </ul>
    </Shell>
  );
}
